import { MapPin, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

// Curated list of popular travel destinations (city + country)
const POPULAR_DESTINATIONS: Array<{
  city: string;
  country: string;
  countryCode: string;
}> = [
  { city: "Paris", country: "France", countryCode: "FR" },
  { city: "Rome", country: "Italy", countryCode: "IT" },
  { city: "Barcelona", country: "Spain", countryCode: "ES" },
  { city: "Amsterdam", country: "Netherlands", countryCode: "NL" },
  { city: "Berlin", country: "Germany", countryCode: "DE" },
  { city: "Vienna", country: "Austria", countryCode: "AT" },
  { city: "Prague", country: "Czech Republic", countryCode: "CZ" },
  { city: "Budapest", country: "Hungary", countryCode: "HU" },
  { city: "Lisbon", country: "Portugal", countryCode: "PT" },
  { city: "Athens", country: "Greece", countryCode: "GR" },
  { city: "Istanbul", country: "Turkey", countryCode: "TR" },
  { city: "Dubai", country: "UAE", countryCode: "AE" },
  { city: "Bangkok", country: "Thailand", countryCode: "TH" },
  { city: "Bali", country: "Indonesia", countryCode: "ID" },
  { city: "Singapore", country: "Singapore", countryCode: "SG" },
  { city: "Tokyo", country: "Japan", countryCode: "JP" },
  { city: "Osaka", country: "Japan", countryCode: "JP" },
  { city: "Seoul", country: "South Korea", countryCode: "KR" },
  { city: "Sydney", country: "Australia", countryCode: "AU" },
  { city: "Melbourne", country: "Australia", countryCode: "AU" },
  { city: "New York", country: "USA", countryCode: "US" },
  { city: "Los Angeles", country: "USA", countryCode: "US" },
  { city: "Miami", country: "USA", countryCode: "US" },
  { city: "Cancún", country: "Mexico", countryCode: "MX" },
  { city: "Rio de Janeiro", country: "Brazil", countryCode: "BR" },
  { city: "Buenos Aires", country: "Argentina", countryCode: "AR" },
  { city: "Cape Town", country: "South Africa", countryCode: "ZA" },
  { city: "Marrakech", country: "Morocco", countryCode: "MA" },
  { city: "Cairo", country: "Egypt", countryCode: "EG" },
  { city: "Nairobi", country: "Kenya", countryCode: "KE" },
  { city: "Mumbai", country: "India", countryCode: "IN" },
  { city: "Delhi", country: "India", countryCode: "IN" },
  { city: "Kathmandu", country: "Nepal", countryCode: "NP" },
  { city: "Colombo", country: "Sri Lanka", countryCode: "LK" },
  { city: "Maldives", country: "Maldives", countryCode: "MV" },
  { city: "Santorini", country: "Greece", countryCode: "GR" },
  { city: "Phuket", country: "Thailand", countryCode: "TH" },
  { city: "Havana", country: "Cuba", countryCode: "CU" },
  { city: "Petra", country: "Jordan", countryCode: "JO" },
  { city: "Reykjavik", country: "Iceland", countryCode: "IS" },
];

interface Suggestion {
  city: string;
  country: string;
  countryCode: string;
  label: string;
  source: "local" | "google";
}

interface DestinationAutocompleteProps {
  city: string;
  country: string;
  onCityChange: (city: string) => void;
  onCountryChange: (country: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
}

export const DestinationAutocomplete: React.FC<
  DestinationAutocompleteProps
> = ({
  city,
  onCityChange,
  onCountryChange,
  label,
  placeholder = "e.g., Barcelona",
  required = false,
}) => {
  const [inputValue, setInputValue] = useState(city);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const autocompleteService = useRef<any>(null);

  // Sync external city value → input
  useEffect(() => {
    setInputValue(city);
  }, [city]);

  // Load Google Places script if API key available
  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (
      !apiKey ||
      apiKey === "your_google_maps_api_key" ||
      window.google?.maps?.places
    ) {
      if (window.google?.maps?.places) setGoogleLoaded(true);
      return;
    }
    const existing = document.getElementById("google-maps-script");
    if (existing) return;

    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.onload = () => {
      setGoogleLoaded(true);
    };
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (googleLoaded && window.google?.maps?.places) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }
  }, [googleLoaded]);

  // Outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const search = async (query: string) => {
    if (query.length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    // Local filter
    const local = POPULAR_DESTINATIONS.filter(
      (d) =>
        d.city.toLowerCase().includes(query.toLowerCase()) ||
        d.country.toLowerCase().includes(query.toLowerCase()),
    )
      .slice(0, 5)
      .map((d) => ({
        ...d,
        label: `${d.city}, ${d.country}`,
        source: "local" as const,
      }));

    if (autocompleteService.current && googleLoaded) {
      try {
        autocompleteService.current.getPlacePredictions(
          { input: query, types: ["(cities)"] },
          (predictions: any[]) => {
            const google_suggestions: Suggestion[] = (predictions ?? [])
              .slice(0, 5)
              .map((p: any) => {
                const parts = p.description.split(", ");
                return {
                  city: parts[0] ?? p.description,
                  country: parts[parts.length - 1] ?? "",
                  countryCode: "",
                  label: p.description,
                  source: "google" as const,
                };
              });
            // Merge: google first, then fill with local up to 6
            const merged = [
              ...google_suggestions,
              ...local.filter(
                (l) =>
                  !google_suggestions.some(
                    (g) => g.city.toLowerCase() === l.city.toLowerCase(),
                  ),
              ),
            ].slice(0, 6);
            setSuggestions(merged);
            setIsOpen(merged.length > 0);
          },
        );
      } catch {
        setSuggestions(local);
        setIsOpen(local.length > 0);
      }
    } else {
      setSuggestions(local);
      setIsOpen(local.length > 0);
    }
  };

  const handleSelect = (s: Suggestion) => {
    setInputValue(s.city);
    onCityChange(s.city);
    if (s.country) onCountryChange(s.country);
    setIsOpen(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, suggestions.length - 1));
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    }
    if (e.key === "Enter" && suggestions[highlightedIndex]) {
      e.preventDefault();
      handleSelect(suggestions[highlightedIndex]);
    }
    if (e.key === "Escape") setIsOpen(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    onCityChange(val);
    setHighlightedIndex(0);
    search(val);
  };

  const handleClear = () => {
    setInputValue("");
    onCityChange("");
    setSuggestions([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative flex items-center">
        <MapPin className="absolute left-3 h-4 w-4 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleChange}
          onFocus={() => {
            if (suggestions.length > 0) setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          required={required}
          className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-lg border border-gray-200 shadow-lg max-h-64 overflow-y-auto">
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              onMouseDown={() => handleSelect(s)}
              onMouseEnter={() => setHighlightedIndex(i)}
              className={`w-full text-left px-4 py-2.5 flex items-center gap-3 text-sm transition-colors ${i === highlightedIndex ? "bg-blue-50 text-blue-900" : "text-gray-800 hover:bg-gray-50"}`}
            >
              <MapPin
                className={`h-4 w-4 shrink-0 ${i === highlightedIndex ? "text-blue-500" : "text-gray-400"}`}
              />
              <div>
                <span className="font-medium">{s.city}</span>
                {s.country && (
                  <span className="text-gray-500 ml-1">– {s.country}</span>
                )}
              </div>
              {s.source === "google" && (
                <span className="ml-auto text-xs text-gray-400">Google</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Extend window for Google Maps types
declare global {
  interface Window {
    google?: {
      maps?: {
        places?: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          AutocompleteService: new () => any;
        };
      };
    };
  }
}
